import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";

import "./index.css";

const THEME_STORAGE_KEY = "pguh:theme";

function restoreSavedTheme() {
  try {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    const theme = savedTheme === "dark" ? "dark" : "light";

    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;

    const themeColour = document.querySelector('meta[name="theme-color"]');
    if (themeColour) {
      themeColour.setAttribute(
        "content",
        theme === "dark" ? "#0b1218" : "#005eb8",
      );
    }
  } catch (error) {
    console.warn("Unable to restore the display theme:", error);
  }
}

restoreSavedTheme();

const LAST_ROUTE_KEY = "pguh:lastRoute";
const LAST_EXACT_ROUTE_KEY = "pguh:lastExactRoute";

function getCurrentRoute() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isSafeRoute(route) {
  return Boolean(
    route &&
      typeof route === "string" &&
      route.startsWith("/") &&
      !route.startsWith("//") &&
      !route.startsWith("/api"),
  );
}

function isGenericIntranetRoute(pathname) {
  return ["/intranet", "/intranet/", "/intranet/portal"].includes(pathname);
}

/*
 * Restore the route before BrowserRouter mounts.
 *
 * The exact page is stored in localStorage while the user browses. If the
 * hosting layer returns the generic intranet portal during a hard refresh,
 * restore the last page immediately so the route guards never see the wrong
 * pathname.
 */
try {
  const navigationEntries =
    window.performance?.getEntriesByType?.("navigation") || [];
  const navigationType = navigationEntries[0]?.type || "navigate";
  const currentRoute = getCurrentRoute();
  const currentPath = window.location.pathname;
  const savedExactRoute = localStorage.getItem(LAST_EXACT_ROUTE_KEY);
  const savedIntranetRoute = localStorage.getItem(LAST_ROUTE_KEY);

  let routeToRestore = null;

  if (navigationType === "reload") {
    if (
      isGenericIntranetRoute(currentPath) &&
      isSafeRoute(savedIntranetRoute) &&
      savedIntranetRoute.startsWith("/intranet/")
    ) {
      routeToRestore = savedIntranetRoute;
    } else if (
      (currentPath === "/" || currentPath === "/home") &&
      isSafeRoute(savedExactRoute) &&
      !savedExactRoute.startsWith("/login")
    ) {
      routeToRestore = savedExactRoute;
    }
  }

  if (routeToRestore && routeToRestore !== currentRoute) {
    window.history.replaceState(window.history.state, "", routeToRestore);
  }
} catch (error) {
  console.warn("Unable to restore the previous route:", error);
}

/*
 * Save the exact browser location immediately before a refresh/navigation.
 * This runs outside React, so the route is preserved even if React is torn
 * down before an effect gets a final chance to run.
 */
window.addEventListener("beforeunload", () => {
  try {
    const route = getCurrentRoute();
    const pathname = window.location.pathname;

    if (isSafeRoute(route) && pathname !== "/login") {
      localStorage.setItem(LAST_EXACT_ROUTE_KEY, route);
    }

    if (pathname.startsWith("/intranet/")) {
      localStorage.setItem(LAST_ROUTE_KEY, route);
    }
  } catch (error) {
    console.warn("Unable to save the current route:", error);
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <App />
  </BrowserRouter>,
);
